import styles from "./checkout.module.css";
import { useCheckout } from "./checkout-provider";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { useEffect } from "react";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { usePaths } from "BEDROCK/paths";
import { useState } from "react";
import { PosterProject } from "PosterEditor/model/poster-project";
import { ProjectPreview } from "components/common/project-preview";
import { useCheckoutPricing } from "hooks/use-checkout-pricing";
import { AlertModal } from "BEDROCK/shared-components/alert-modal";
import { ActivityIndicator } from "BEDROCK/shared-components/activity-indicator";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";
import { sizeInInchesFromPreset } from "PosterEditor/model/poster-project";
import { useProjectFormatter } from "hooks/use-project-formatter";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { first } from "lodash";
import { AddressModal } from "BEDROCK/shared-components/address-modal";
import {
  ShippingAddress,
  useAddressStore,
} from "entity-store/address-store/use-address-store";

type CheckoutState = "empty" | "loading" | "ready" | "processing" | "error";

interface CheckoutProps {}

export const Checkout = (props: CheckoutProps) => {
  const [state, setState] = useState<CheckoutState>("ready");
  const { checkout, removeItem, pay, clearAllAndDelete } = useCheckout();
  const { checkoutProjects, isCheckoutLoading } = useProjectStore();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [selectedAddress, setSelectedAddress] =
    useState<ShippingAddress | null>(null);
  const { defaultAddress, isLoadingDefault } = useAddressStore();

  // Load default address when available and no address is selected
  useEffect(() => {
    if (defaultAddress && !selectedAddress) {
      setSelectedAddress(defaultAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddress]);

  useEffect(() => {
    if (checkout.items.length === 0) {
      setState("empty");
    } else {
      setState("ready");
    }
  }, [checkout.items.length]);

  const handleAddressConfirm = (address: ShippingAddress) => {
    setShowAddressModal(false);
    setSelectedAddress(address);
  };

  const handleClickCheckout = () => {
    if (!selectedAddress) {
      // No address, open modal
      setShowAddressModal(true);
    } else {
      // Have address, proceed to payment
      setState("processing");
      pay(selectedAddress).catch((error) => {
        console.error("Error during payment:", error);
        setState("error");
      });
    }
  };

  const handleClearAll = async () => {
    setState("processing");
    try {
      await clearAllAndDelete();
      setState("empty");
    } catch (error) {
      console.error("Error clearing checkout:", error);
      setState("error");
    }
  };

  if (isCheckoutLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.listContainer}>
          <div className={styles.listHeader}>
            <h1>CHECKOUT</h1>
          </div>
          <div className={styles.emptyState}>
            <ActivityIndicator size="medium" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ItemList
        state={state}
        projects={checkoutProjects ?? []}
        onRemoveItem={async (item) => {
          await removeItem(item);
          // State will be updated by useEffect when checkout.items changes
        }}
        selectedAddress={selectedAddress}
        onClickAddress={() => setShowAddressModal(true)}
        onClickCheckout={handleClickCheckout}
        onClearAll={handleClearAll}
      />
      <AddressModal
        isShowing={showAddressModal}
        onConfirm={handleAddressConfirm}
        onDismiss={() => setShowAddressModal(false)}
        currentAddress={selectedAddress}
      />
    </div>
  );
};

interface ItemListProps {
  state: CheckoutState;
  projects: PosterProject[];
  onRemoveItem: (id: string) => Promise<void>;
  selectedAddress: ShippingAddress | null;
  onClickAddress: () => void;
  onClickCheckout: () => void;
  onClearAll: () => void;
}

const ItemList = (props: ItemListProps) => {
  const { formattedPriceForProjects } = useCheckoutPricing();
  const { role } = useAuthContext();
  const isAdmin = role === "admin";
  const [isShowingClearAllModal, setIsShowingClearAllModal] = useState(false);
  const { navigateTo, firstCollectionList } = usePaths();
  const isEmpty = props.projects.length === 0;
  const totalPrice = formattedPriceForProjects(props.projects);

  const handleClearAll = () => {
    setIsShowingClearAllModal(false);
    props.onClearAll();
  };

  return (
    <div className={styles.listContainer}>
      <div className={styles.listHeader}>
        <h1>CHECKOUT</h1>
        {isAdmin && props.projects.length > 0 && (
          <button
            className={styles.clearAllButton}
            onClick={() => setIsShowingClearAllModal(true)}
          >
            CLEAR ALL
          </button>
        )}
      </div>

      {isEmpty ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateMessage}>
            Your checkout is empty. Browse our preset designs to create a
            project.
          </p>
          <CTAButton
            variant="PRIMARY"
            onClick={() => navigateTo(firstCollectionList)}
          >
            Browse Designs
          </CTAButton>
        </div>
      ) : (
        <div className={styles.checkoutContent}>
          <div className={styles.itemsColumn}>
            <div className={styles.list}>
              {props.projects.map((project) => (
                <ListItem
                  key={project.id}
                  project={project}
                  onRemove={async () => {
                    await props.onRemoveItem(project.id);
                  }}
                />
              ))}
            </div>
            <div className={styles.totalSection}>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Total</span>
                <span className={styles.totalPrice}>{totalPrice}</span>
              </div>
              <div className={styles.deliveryInfo}>
                Standard delivery 4-6 business days (included)
              </div>
            </div>
          </div>

          <div className={styles.checkoutColumn}>
            <div className={styles.addressSection}>
              <h3 className={styles.addressTitle}>Delivery Address</h3>
              {props.selectedAddress ? (
                <div
                  className={styles.addressDisplay}
                  onClick={props.onClickAddress}
                  role="button"
                  tabIndex={0}
                >
                  <p>{props.selectedAddress.line1}</p>
                  {props.selectedAddress.line2 && (
                    <p>{props.selectedAddress.line2}</p>
                  )}
                  <p>{props.selectedAddress.city}</p>
                  <p>{props.selectedAddress.postcode}</p>
                  <button className={styles.changeAddressLink}>
                    Change address
                  </button>
                </div>
              ) : (
                <div
                  className={styles.addAddressButton}
                  onClick={props.onClickAddress}
                  role="button"
                  tabIndex={0}
                >
                  + Add delivery address
                </div>
              )}
            </div>

            <div className={styles.checkoutControls}>
              {props.selectedAddress && (
                <a href="https://stripe.com" target="_blank" rel="noreferrer">
                  <img
                    className={styles.stripeLogo}
                    src="/checkout/stripe.svg"
                    alt="Powered by Stripe"
                    width={150}
                    height={34}
                  />
                </a>
              )}
              {props.state === "ready" && (
                <CTAButton
                  variant="PRIMARY"
                  onClick={props.onClickCheckout}
                  className={styles.checkoutButton}
                  iconName="CHEVRON-RIGHT"
                  iconPosition="RIGHT"
                >
                  {props.selectedAddress
                    ? "Continue to payment"
                    : "Enter address"}
                </CTAButton>
              )}
              {props.state === "processing" && (
                <ActivityIndicator size="small" />
              )}
            </div>
          </div>
        </div>
      )}
      <AlertModal
        title="Clear entire checkout"
        message="Are you sure you want to delete all projects in checkout? This will permanently delete all items and cannot be undone."
        isShowing={isShowingClearAllModal}
        onDismiss={() => setIsShowingClearAllModal(false)}
        dismissCTA="CANCEL"
        proceedCTA="CLEAR ALL"
        onProceed={handleClearAll}
      />
    </div>
  );
};

interface ListItemProps {
  project: PosterProject;
  onRemove: () => Promise<void>;
}

const ListItem = (props: ListItemProps) => {
  const { project } = props;
  const { formattedPriceForProject } = useCheckoutPricing();
  const [isShowingDeleteAlertModal, setIsShowingDeleteAlertModal] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { navigateTo, mapEditor } = usePaths();
  const { sizePresetFromProject } = useSizePresets();

  const removeItem = async () => {
    setIsShowingDeleteAlertModal(false);
    setIsDeleting(true);
    await props.onRemove();
    // Note: component will unmount after deletion, so no need to set isDeleting to false
  };

  const onClickEdit = () => {
    navigateTo(mapEditor + "/" + project.id);
  };

  const price = formattedPriceForProject(project);
  const preset = sizePresetFromProject(project);

  const inchSize = sizeInInchesFromPreset(project.size);
  const inchSizeString = `${inchSize.width}" x ${inchSize.height}"`;

  return (
    <div className={styles.listItem}>
      <div className={styles.listItemPreviewAsset}>
        <ProjectPreview
          project={project}
          size={{ width: 140, height: 140 }}
          showOutline
          fallbackToMapImage={false}
        />
      </div>
      <div className={styles.listItemDescriptionContainer}>
        <h1>{preset?.materialDescription ?? "Print"}</h1>
        <h3>
          <b>{preset?.sizeDescription ?? ""}</b>
        </h3>
      </div>
      <div className={styles.listItemtTrailingContainer}>
        <span className={styles.price}>{price}</span>
        <button
          className={styles.button}
          onClick={() => setIsShowingDeleteAlertModal(true)}
        >
          REMOVE
        </button>
        <button className={styles.button} onClick={onClickEdit}>
          EDIT
        </button>
      </div>
      <AlertModal
        title="Remove & delete item"
        message="Are you sure you want to remove this project from your checkout and delete?  This action cannot be undone."
        isShowing={isShowingDeleteAlertModal}
        onDismiss={() => setIsShowingDeleteAlertModal(false)}
        dismissCTA="CANCEL"
        proceedCTA="REMOVE"
        onProceed={removeItem}
      />
    </div>
  );
};
